<template>
  <BaseSideModal :width="'820px'" @close="$emit('close')">
    <template #title>
      <span>{{ $t('carriers.import.runRows.Title') }}</span>
    </template>

    <!-- See AllUpdatesDrawer: a second line in the title slot overflows the
         modal, because its content height assumes a one-line header. -->
    <p class="run-rows__subtitle">
      {{ carrierName }} · {{ formatWhen(run.finishedAt || run.updatedAt, $i18n.locale) }}
    </p>

    <!-- One tab per answer, each counted by the server over the whole read, so
         the numbers are the ones on the run's own line. -->
    <div class="run-rows__tabs">
      <BaseTabs>
        <BaseTabsTab
          v-for="t in tabs"
          :key="t.key"
          :active="filter === t.key"
          :badge-text="t.count === null ? '' : formatCount(t.count)"
          @click="switchFilter(t.key)"
        >
          {{ $t(`carriers.import.runRows.filter.${t.key}`) }}
        </BaseTabsTab>
      </BaseTabs>
    </div>

    <div class="run-rows__search">
      <InputSearch
        :value="search"
        :placeholder="$t('carriers.import.runRows.Search')"
        :opened="true"
        @searchUpdate="onSearch"
      />
    </div>

    <BasePreloader v-if="loading && !rows.length" color="purple" size="l"/>

    <p v-else-if="loadFailed && !rows.length" class="run-rows__empty">
      {{ $t('adminTitles.someError') }}
      <BaseButton type="outline" is-small @click="reload">{{ $t('adminTitles.TryAgain') }}</BaseButton>
    </p>

    <p v-else-if="!rows.length" class="run-rows__empty">{{ $t('carriers.import.runRows.Empty') }}</p>

    <template v-else>
      <div v-for="item in shown" :key="item.row.id" class="run-rows__row">
        <div class="run-rows__main">
          <p class="run-rows__policy">
            <a
              v-if="item.row.insurancePolicyId"
              class="run-rows__policy-link"
              :href="policyHref(item.row.insurancePolicyId)"
              target="_blank"
              rel="noopener"
              :title="$t('carriers.import.snapshot.OpenPolicy')"
            >{{ item.row.policyNumber }} ↗</a>
            <template v-else>{{ item.row.policyNumber }}</template>
          </p>
          <p v-if="item.row.clientName" class="run-rows__client">{{ item.row.clientName }}</p>

          <p v-if="item.result.hint" class="run-rows__why">{{ $t(item.result.hint) }}</p>
          <p v-if="item.result.paymentsNote" class="run-rows__why">{{ item.result.paymentsNote }}</p>
          <p v-for="(why, i) in item.result.reasons" :key="i" class="run-rows__why">{{ whyText(why) }}</p>
          <p v-if="item.result.error" class="run-rows__failure">{{ item.result.error }}</p>

          <p v-if="item.changes" class="run-rows__changes">{{ item.changes }}</p>
        </div>

        <span class="run-rows__result" :class="`run-rows__result--${item.result.tone}`">
          {{ $t(item.result.label) }}
        </span>
      </div>

      <!-- The next page of 50 arrives when this scrolls into view. -->
      <div v-if="hasMore" ref="more" class="run-rows__more">
        <BasePreloader v-if="loading" color="purple" size="s"/>
      </div>
    </template>

    <template #footer>
      <BaseButton type="outline" @click="$emit('close')">{{ $t('buttons.back') }}</BaseButton>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import BaseTabs from '@/views/RenovaDashboard/components/ui-kit/BaseTabs';
import BaseTabsTab from '@/views/RenovaDashboard/components/ui-kit/BaseTabsTab';
import InputSearch from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputSearch';
import { apiError, formatCount, formatWhen } from '@/utils/carrierImport';
import { formatCarrierDate } from '@/utils/carrierFormat';
import { RUN_ROW_FILTERS, runRowResult } from '@/utils/carrierPayments';

const PAGE = 50;
const SEARCH_DELAY = 300;
/** Shown whether or not they hold anything: the answers someone opens this to find. */
const ALWAYS = ['ALL', 'UPDATED', 'UP_TO_DATE', 'NOT_IN_RENOVA'];

/**
 * Every policy a payments read went through, and what it did with each one.
 *
 * The run's own line only counts ("32 policies read · 0 payment schedules
 * updated · 29 not found in Renova"); this is the list behind the counts. Any
 * carrier: the reconcile writes one row per policy per read for Allianz, AXA and
 * Skandia alike, and the review screens only ever show the rows still waiting.
 *
 * Read-only. Decisions stay where they are made -- the held policies on the
 * run's card, the Allianz review -- and this says what is waiting, not how to
 * answer it. Every filter and the search are the server's, so a count is a
 * `totalCount` over the whole read and paging stops where it should.
 */
export default {
  name: 'PaymentRunPoliciesModal',
  components: {
    BaseSideModal, BaseButton, BasePreloader, BaseTabs, BaseTabsTab, InputSearch,
  },
  props: {
    /** The payments run, with its `scrapeJobId`. */
    run: { type: Object, required: true },
    carrierName: { type: String, default: '' },
  },
  emits: ['close'],
  data() {
    return {
      rows: [],
      /** A key of RUN_ROW_FILTERS. */
      filter: 'ALL',
      search: '',
      /** The next page to ask for, and how many the filter holds (null: not asked yet). */
      page: 0,
      total: null,
      loading: true,
      loadFailed: false,
      /** Filter key -> its count, for the tabs; null until it is in. */
      counts: {},
      /** Which load() is current; only the latest may touch the list (see SnapshotReviewModal). */
      loadSeq: 0,
      countSeq: 0,
      searchTimer: null,
      observer: null,
    };
  },
  computed: {
    hasMore() {
      return this.total === null || this.rows.length < this.total;
    },
    tabs() {
      return Object.keys(RUN_ROW_FILTERS)
        .map((key) => ({ key, count: key in this.counts ? this.counts[key] : null }))
        .filter((t) => ALWAYS.includes(t.key) || t.key === this.filter || t.count > 0);
    },
    /** Each row with its result worked out once, not once per binding. */
    shown() {
      return this.rows.map((row) => {
        const result = runRowResult(row);
        return { row, result, changes: this.changesText(result) };
      });
    },
    query() {
      return {
        scrapeJobId: this.run.scrapeJobId,
        searchQuery: this.search.trim() || undefined,
      };
    },
  },
  created() {
    this.reload();
  },
  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0] && entries[0].isIntersecting && !this.loading && !this.loadFailed && this.hasMore) this.load();
    });
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatCount,
    formatWhen,
    policyHref(id) {
      return this.$router.resolve({ name: 'policy', params: { id } }).href;
    },
    /** The same words the held policies on the run's card use. */
    whyText(why) {
      const carrier = this.carrierName;
      if (why.kind === 'applyError') return this.$t('carriers.import.held.ApplyFailed', { error: why.message });
      if (why.kind === 'keptInRenova') {
        const key = `${why.status === 'CANCELED' ? 'KeptCanceled' : 'KeptPaid'}${why.receipt ? '' : 'Unlisted'}`;
        return this.$t(`carriers.import.held.${key}`, { date: formatCarrierDate(why.dueDate), carrier });
      }
      const params = { row: why.row, carrier, reason: why.reason };
      return why.reason
        ? this.$t('carriers.import.held.Unreadable', params)
        : this.$t('carriers.import.held.UnreadableNoReason', params);
    },
    /** "3 payments marked paid · 1 payments added · savings figures refreshed", or "If you apply: …". */
    changesText(result) {
      const pieces = result.changes.map((p) => `${formatCount(p.count)} ${this.$t(p.label)}`);
      if (result.savingsUpdated) pieces.push(this.$t('carriers.import.runRows.SavingsRefreshed'));
      if (!pieces.length) return '';
      const text = pieces.join(' · ');
      return result.changesLead ? this.$t('carriers.import.held.IfApplied', { changes: text }) : text;
    },
    switchFilter(key) {
      if (key === this.filter) return;
      this.filter = key;
      this.load(true);
    },
    onSearch(value) {
      this.search = value || '';
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, SEARCH_DELAY);
    },
    reload() {
      this.load(true);
      this.countAll();
    },
    /** One row per filter, for its `totalCount`: the tabs' badges, narrowed by the search like the list. */
    countAll() {
      this.countSeq += 1;
      const seq = this.countSeq;
      this.counts = {};
      Object.keys(RUN_ROW_FILTERS).forEach((key) => {
        this.$store.dispatch('carrierImportFetchPaymentRows', {
          ...this.query, ...RUN_ROW_FILTERS[key], page: 0, limit: 1,
        })
          .then((data) => {
            if (seq !== this.countSeq) return;
            this.counts = { ...this.counts, [key]: Number(data && data.totalCount) || 0 };
          })
          .catch(() => {});
      });
    },
    /** The next page of the filter showing, or its first after a change. */
    load(reset = false) {
      if (reset) {
        this.rows = [];
        this.page = 0;
        this.total = null;
      }
      this.loading = true;
      this.loadFailed = false;
      this.loadSeq += 1;
      const seq = this.loadSeq;
      return this.$store.dispatch('carrierImportFetchPaymentRows', {
        ...this.query, ...RUN_ROW_FILTERS[this.filter], page: this.page, limit: PAGE,
      })
        .then((data) => {
          if (seq !== this.loadSeq) return;
          const items = (data && data.items) || [];
          this.rows = this.rows.concat(items);
          this.page += 1;
          // A short page is the last, whatever the total says.
          this.total = items.length < PAGE ? this.rows.length : (Number(data && data.totalCount) || 0);
        })
        .catch((err) => {
          if (seq !== this.loadSeq) return;
          this.loadFailed = true;
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
        })
        .finally(() => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.$nextTick(this.watchBottom);
        });
    },
    watchBottom() {
      if (!this.observer) return;
      this.observer.disconnect();
      if (this.$refs.more) this.observer.observe(this.$refs.more);
    },
  },
};
</script>

<style lang="scss" scoped>
// Success text has no token in the app's palette -- the same literal ImportRunList uses.
$ink-success: #2e7d32;

.run-rows {
  &__subtitle {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  // BaseTabs floats; flow-root keeps it inside the drawer's flow.
  &__tabs {
    display: flow-root;
    margin-bottom: 12px;
  }

  &__search {
    margin-bottom: 8px;
  }

  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-of-type { border-bottom: 0; }
  }

  &__main { flex: 1; min-width: 0; }

  &__policy {
    font-size: 14px;
    font-weight: 600;
    color: var(--solid-black-purple);
  }

  &__policy-link {
    color: inherit;
    text-decoration: underline;
    text-decoration-color: var(--solid-lavender-grey);
    text-underline-offset: 2px;

    &:hover { color: var(--solid-purple); text-decoration-color: currentColor; }
  }

  &__client {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 2px;
  }

  &__why {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 6px;
  }

  &__changes {
    font-size: 12.5px;
    color: var(--solid-black-purple);
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
  }

  &__failure {
    font-size: 12.5px;
    color: var(--solid-red);
    margin-top: 6px;
  }

  &__result {
    flex: none;
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;

    &--done { color: $ink-success; }
    &--ok { color: var(--solid-green); }
    &--warn { color: var(--solid-orange); }
    &--error { color: var(--solid-red); }
    &--muted { color: var(--solid-dark-purple); }
  }

  // Tall enough to be seen by the observer before the spinner is in it.
  &__more {
    display: flex;
    justify-content: center;
    min-height: 32px;
    margin-top: 12px;
  }
}
</style>
